import { styled } from 'canoe';
import React from 'react';

import { FormInputValidation } from '@candu-ai/forms/document-types';
import { useManageFormFieldValidation } from '@candu-ai/forms/editor-utils';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import Node from 'craft/components/Node';
import { BuiltInComponents } from 'craft/constants';
import { useEditorNode, useNodeContext } from 'craft/hooks';
import { useFormAncestor, useFormIdFromAncestor } from 'craft/state';
import { CssSelectorId, Resolver, ToolboxComponentSections } from 'craft/types';

import { FormFieldValidationMessage } from './FormFieldValidationMessage';
import { FormLabel } from './FormLabel';
import { getFormFieldNodeRules } from './helpers';
import { SelectInputToolbox } from './toolbox/SelectInputToolbox';

// TODO: These flex settings should likely be on the node when dragged in from the Palette
// they can be removed from the SDK then too
const FormSelectInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

interface Props {
  formInputId: string;
  formId: string;
  label: string;
  placeholder?: string;
  showLabel?: boolean;

  options: { label: string; value?: string }[];
  validation?: FormInputValidation[];
}

const selectClassName = toCssSelector('form', {
  output: 'html',
  className: 'inputField',
});

export const FormSelectInput: Resolver = ({
  formInputId,
  formId,
  label,
  placeholder,
  showLabel,
  options,
  validation,
}: Props) => {
  const { id } = useNodeContext();
  const { shouldDisplayErrorState } = useFormAncestor(id);
  const { setProps } = useEditorNode(id);

  useFormIdFromAncestor(formId);

  const {
    validationIndexMapping,
    isRequired,
    onSetAtomicValidationProps,
  } = useManageFormFieldValidation({
    setProps,
    validation,
  });

  const classNames = `${selectClassName} ${shouldDisplayErrorState ? ' error' : ''}`.trim();

  return (
    <Node is={FormSelectInputContainer} data-type="form-select-input">
      {showLabel && <FormLabel id={id} required={isRequired} label={label} />}

      <select
        id={formInputId}
        className={classNames}
        value=""
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {options.map((option, i) => (
          <option key={i.toString()} value={option.value || option.label}>
            {option.label}
          </option>
        ))}
      </select>
      {isRequired && shouldDisplayErrorState && (
        <FormFieldValidationMessage
          nodeId={id}
          validationKey="required"
          onSetProps={(value) => onSetAtomicValidationProps(value, 'required')}
          message={validation?.[validationIndexMapping.required]?.message}
        />
      )}
    </Node>
  );
};

FormSelectInput.craft = {
  displayName: 'Form: Dropdown Input',
  name: BuiltInComponents.FormSelectInput,
  isCanvas: false,
  rules: getFormFieldNodeRules(),
};

FormSelectInput.toolbox = {
  component: [SelectInputToolbox],
  schema: {
    [ToolboxComponentSections.Border]: {
      cssSelectorId: CssSelectorId.FormSelectInput,
    },
    [ToolboxComponentSections.Shadow]: {
      cssSelectorId: CssSelectorId.FormSelectInput,
    },
  },
};

FormSelectInput.selectedToolbar = {};
